// Servizi Area Clienti (ruolo `customer`).
import { api, unwrap } from './client';

/** @typedef {import('@/types/api').components['schemas']} S */

export const customerService = {
  /** @param {S['CustomerRegisterRequest']} body */
  register: (body) => unwrap(api.post('/customer/register', body)),

  /** Profilo del cliente loggato. */
  getProfile: () => unwrap(api.get('/customer/profile')),

  /** @param {S['CustomerProfileUpdate']} body */
  updateProfile: (body) => unwrap(api.put('/customer/profile', body)),

  /** @param {S['CustomerPasswordChange']} body */
  changePassword: (body) => unwrap(api.post('/customer/change-password', body)),

  /** Prenotazioni aggregate (hotel + ristorante) del cliente. */
  getBookings: () => unwrap(api.get('/customer/me/bookings')),

  /** Elenco preferiti. */
  getFavorites: () => unwrap(api.get('/customer/favorites')),

  /** @param {S['CustomerFavoriteRequest']} body */
  addFavorite: (body) => unwrap(api.post('/customer/favorites', body)),

  /** @param {string} structureId */
  removeFavorite: (structureId) => unwrap(api.delete(`/customer/favorites/${structureId}`)),

  /** Dati fiscali del cliente (validi per tutti i verticali). */
  getBilling: () => unwrap(api.get('/customer/billing')),

  /** Salva/aggiorna i dati fiscali del cliente. */
  saveBilling: (body) => unwrap(api.put('/customer/billing', body)),
};

// Config per upload multipart: Content-Type undefined => axios imposta il boundary corretto.
const MULTIPART = { headers: { 'Content-Type': undefined } };

/**
 * Area Clienti — Hotel (stile Airbnb): dettaglio prenotazione, self check-in (H3),
 * documenti identita' (GridFS), chat avanzata con allegati e servizi extra.
 */
export const customerHotelService = {
  /** Dettaglio completo della prenotazione hotel. */
  getBooking: (bid) => unwrap(api.get(`/customer/hotel/bookings/${bid}`)),

  /** Verbale di consegna/riconsegna (stato immobile) — vista ospite + firma/difformità. */
  handoverGet: (bid) => unwrap(api.get(`/hotel/handover/guest/booking/${bid}`)),
  handoverConfirm: (bid, phase, body) => unwrap(api.post(`/hotel/handover/guest/booking/${bid}/confirm?phase=${encodeURIComponent(phase || 'consegna')}`, body)),
  handoverDispute: (bid, phase, body) => unwrap(api.post(`/hotel/handover/guest/booking/${bid}/dispute?phase=${encodeURIComponent(phase || 'consegna')}`, body)),

  /** Feedback CETS cliente (mini-questionario post-esperienza). source: hotel|restaurant|experience|shop */
  cetsFeedbackGet: (source, refId) => unwrap(api.get(`/cets/feedback/${source}/${refId}`)),
  cetsFeedbackSubmit: (source, refId, body) => unwrap(api.post(`/cets/feedback/${source}/${refId}`, body)),

  /** Invia i dati del self check-in (H3). */
  submitSelfCheckIn: (bid, body) => unwrap(api.post(`/customer/hotel/bookings/${bid}/self-check-in`, body)),

  /** Documenti d'identita'. */
  listDocuments: (bid) => unwrap(api.get(`/customer/hotel/bookings/${bid}/documents`)),
  uploadDocument: (bid, side, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return unwrap(api.post(`/customer/hotel/bookings/${bid}/documents/${side}`, fd, MULTIPART));
  },
  deleteDocument: (bid, docId) => unwrap(api.delete(`/customer/hotel/bookings/${bid}/documents/${docId}`)),

  /** Chat con la struttura. */
  getChat: (bid) => unwrap(api.get(`/customer/hotel/bookings/${bid}/chat`)),
  sendChat: (bid, text) => unwrap(api.post(`/customer/hotel/bookings/${bid}/chat`, { text })),
  sendChatAttachment: (bid, file, text = '') => {
    const fd = new FormData();
    fd.append('file', file);
    if (text) fd.append('text', text);
    return unwrap(api.post(`/customer/hotel/bookings/${bid}/chat/attachment`, fd, MULTIPART));
  },
  markChatRead: (bid) => unwrap(api.post(`/customer/hotel/bookings/${bid}/chat/read`)),

  /** Servizi extra (catalogo reale del CRM + richiesta con prezzo server-side). */
  listExtras: (ownerId) => unwrap(api.get(`/customer/hotel/structures/${ownerId}/extras`)),
  requestExtra: (bid, extraId, qty = 1) => unwrap(api.post(`/customer/hotel/bookings/${bid}/extras`, { extra_id: extraId, qty })),
  cancelExtra: (bid, itemId) => unwrap(api.delete(`/customer/hotel/bookings/${bid}/extras/${itemId}`)),

  /** Cancellazione: l'ospite richiede l'annullamento (la struttura approva/rifiuta). */
  requestCancellation: (bid, reason = '') => unwrap(api.post(`/customer/hotel/bookings/${bid}/cancel-request`, { reason })),

  /** Recensione post-soggiorno (disponibile dopo il check-out). */
  submitReview: (bid, rating, comment = '') => unwrap(api.post(`/customer/hotel/bookings/${bid}/review`, { rating, comment })),

  /** Ricevuta di prenotazione in PDF (documento NON fiscale). Ritorna la risposta axios (blob). */
  downloadReceipt: (bid) => api.get(`/customer/hotel/bookings/${bid}/receipt`, { responseType: 'blob' }),

  /** Fattura (bozza) in PDF. Ritorna la risposta axios (blob). Richiede dati fiscali completi. */
  downloadInvoice: (bid) => api.get(`/customer/hotel/bookings/${bid}/invoice`, { responseType: 'blob' }),
};


/**
 * Area Clienti — Esperienze/Eventi: biglietti acquistati (QR, PDF), dettaglio e cancellazione.
 */
export const customerExperienceService = {
  /** Elenco ordini/biglietti del cliente. */
  getOrders: () => unwrap(api.get('/customer/experience/orders')),

  /** Dettaglio ordine (con info esperienza + eleggibilità cancellazione). */
  getOrder: (id) => unwrap(api.get(`/customer/experience/orders/${id}`)),

  /** Annulla un ordine (rimborso mock secondo policy). */
  cancelOrder: (id, reason = '') => unwrap(api.post(`/customer/experience/orders/${id}/cancel`, { reason })),

  /** PDF dei biglietti (con QR). Ritorna la risposta axios (blob). */
  downloadTickets: (id, lang) => api.get(`/customer/experience/orders/${id}/tickets.pdf`, { params: { lang: lang || (typeof window !== 'undefined' ? (localStorage.getItem('sila_lang') || 'it') : 'it') }, responseType: 'blob' }),
};


/**
 * Area Clienti — Biglietti EVENTI + rimborso self-service (regole organizzatore).
 */
export const customerEventService = {
  /** Elenco ordini biglietti eventi del cliente (con idoneità rimborso). */
  getOrders: () => unwrap(api.get('/events/customer/orders')),

  /** Richiede il rimborso di un ordine biglietti secondo le regole dell'organizzatore. */
  requestRefund: (orderId, reason = '') => unwrap(api.post(`/events/customer/orders/${orderId}/refund`, { reason })),

  /** Ricevuta PDF di un ordine (via codice). Ritorna URL pubblico. */
  receiptUrl: (code) => `/events/public/orders/${code}/receipt.pdf`,
};


/**
 * Area Clienti — Servizi Staff (Bridge 1): catalogo, operatori per zona,
 * slot liberi e prenotazione diretta. Pagamenti/preventivo MOCK.
 */
export const customerServicesService = {
  /** Catalogo: servizi + zone attivi. */
  getCatalog: () => unwrap(api.get('/customer/services/catalog')),

  /** Operatori disponibili per servizio + zona. */
  getOperators: (serviceId, zoneId) =>
    unwrap(api.get('/customer/services/operators', { params: { service_id: serviceId, zone_id: zoneId } })),

  /** Slot liberi dell'operatore (from = YYYY-MM-DD, days 1..14). */
  getSlots: (staffId, from, days = 7) =>
    unwrap(api.get(`/customer/services/operators/${staffId}/slots`, { params: { from, days } })),

  /** Prenota uno slot. */
  book: (body) => unwrap(api.post('/customer/services/book', body)),

  /** Prenotazioni servizi del cliente. */
  getMyBookings: () => unwrap(api.get('/customer/services/me/bookings')),

  /** Annulla una prenotazione (libera lo slot). */
  cancelBooking: (id) => unwrap(api.post(`/customer/services/me/bookings/${id}/cancel`)),

  /** Operatori preferiti del cliente (F2). */
  getFavorites: () => unwrap(api.get('/customer/services/favorites')),

  /** Aggiunge/rimuove un operatore dai preferiti (F2). */
  toggleFavorite: (staffId) => unwrap(api.post('/customer/services/favorites/toggle', { staff_id: staffId })),
};


/**
 * Recensioni post-attività (F1): escursioni + servizi. Doppia valutazione (attività + operatore).
 */
export const reviewsService = {
  /** Attività concluse e non ancora recensite dal cliente. */
  getPending: () => unwrap(api.get('/customer/reviews/pending')),

  /** Crea una recensione ({ kind, ref_id, rating_activity, rating_operator, comment }). */
  create: (body) => unwrap(api.post('/customer/reviews', body)),

  /** Le recensioni lasciate dal cliente. */
  getMine: () => unwrap(api.get('/customer/reviews/me')),

  /** Recensioni pubbliche escursioni (pagina Parchi) — nessuna autenticazione richiesta. */
  getParkReviews: (limit = 20) => unwrap(api.get('/parco/public/reviews', { params: { limit } })),
};


/**
 * Area Clienti — Escursioni acquistate (Bridge 3): mappa live GPS + SOS.
 */
export const customerExcursionsService = {
  /** Elenco escursioni acquistate dal cliente (con rotta GeoJSON e punti). */
  getAll: () => unwrap(api.get('/customer/excursions')),

  /** Dettaglio di una singola escursione. */
  get: (orderId) => unwrap(api.get(`/customer/excursions/${orderId}`)),

  /** Invia un SOS con la posizione GPS corrente (allerta guida + centrale). */
  sos: (orderId, body) => unwrap(api.post(`/customer/excursions/${orderId}/sos`, body)),

  /** PDF dei biglietti escursione (blob, autenticato via token). */
  downloadPdf: (orderId) => api.get(`/parco/public/orders/${orderId}/tickets.pdf`, { responseType: 'blob' }),
};
